import React from "react";
import { XCircle } from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useAppDispatch } from "../../../hooks";
import { fetchProducts, restockProducts } from "../../../slices/productSlice";
import { toast } from "react-toastify";

interface RestockModalProps {
  onClose: () => void;
  product?: { id: string, name: string };
}

interface RestockFormValues {
  quantity: number | string;
  mfg_date: string;
  exp_date: string;
}

const RestockModal: React.FC<RestockModalProps> = ({ onClose, product }) => {
  const initialValues: RestockFormValues = {
    quantity: "",
    mfg_date: "",
    exp_date: "",
  };

  const dispatch = useAppDispatch();

  const validationSchema = Yup.object({
    quantity: Yup.number()
      .typeError("Must be a number")
      .positive("Quantity must be positive")
      .required("Quantity is required"),
    mfg_date: Yup.date()
      .required("Manufacture date is required")
      .max(new Date(), "Manufacture date cannot be in the future"),
    exp_date: Yup.date()
      .required("Expiry date is required")
      .min(Yup.ref("mfg_date"), "Expiry must be after manufacture date"),
  });

  const handleSubmit = async (values: RestockFormValues) => {
    console.log("Restock data:", values, product);

    const restockData = {
      ...values, // mfg date & exp date
      quantity: values.quantity as number,
      product_id: product?.id as string
    }

    try {
      await dispatch(restockProducts([restockData])).unwrap();
      await dispatch(fetchProducts()).unwrap();
      toast.success(`Successfully restocked ${product?.name}`);
      onClose();
    } catch (error) {
      console.error("Restock failed:", error);
      toast.error("Failed to restock product. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md p-6 relative shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-800"
        >
          <XCircle color="red" size={20} />
        </button>

        <h2 className="text-lg font-bold mb-4">
          Restock {product?.name ? `"${product.name}"` : "Product"}
        </h2>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-3 text-left">
              <div>
                <label className="form-label">
                  Quantity
                  <Field
                    type="number"
                    name="quantity"
                    placeholder="Quantity"
                    className="w-full border rounded-md px-3 py-2"
                  />
                  <ErrorMessage
                    name="quantity"
                    component="div"
                    className="text-xs text-red-500 mt-1"
                  />
                </label>
              </div>

              <div className="grid md:grid-cols-2">
                <div>
                  <label className="form-label">Manufacture Date</label>
                  <Field
                    type="date"
                    name="mfg_date"
                    className="w-full border rounded-md px-3 py-2 mt-1"
                  />
                  <ErrorMessage
                    name="mfg_date"
                    component="div"
                    className="text-xs text-red-500 mt-1"
                  />
                </div>

                <div>
                  <label className="form-label">Expiry Date</label>
                  <Field
                    type="date"
                    name="exp_date"
                    className="w-full border rounded-md px-3 py-2 mt-1"
                  />
                  <ErrorMessage
                    name="exp_date"
                    component="div"
                    className="text-xs text-red-500 mt-1"
                  />
                </div>
              </div>

              {/* BUTTONS */}
              <div className="flex justify-end gap-2 mt-2">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border">
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="form-btn-mini"
                >
                  {isSubmitting ? "Saving..." : "Restock"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default RestockModal;
